<template>
	<div class='no-error-icons'>
		<Select class='mt-2' selectClass='!bg-grey text-15' menuClass='!bg-grey' name='category_id' 
			label='Deal category' placeholder='Deal category' 
			v-model:value='value.category_id' :options='options'
		/>

        <button class='flex items-center mt-3' type='button' @click='addName = true' v-if='!addName'>
			<IconPlus class='stroke-black w-4.5 h-4.5' />
			<p class='pl-2 text-grey-900 text-14'>Add a custom name</p>
		</button>

		<Input placeholder='Custom name' name='name'
			v-if='addName'
			v-model:value='value.name'
			class='mt-2'
			ref='cName'
            groupClass='bg-grey text-15'>

            <template #right>
            	<button @click='addName = false; value.name = null;' class='w-10 h-6 flex items-center justify-center'>
            		<IconClose class='stroke-black w-4.5 h-4.5' />
            	</button>
            </template>
        </Input>

        <Textarea label='Summary' v-model:value='value.summary'
        	name='summary' rows='4'
        	class='mt-4' placeholder='What is this deal about?'
        	groupClass='bg-grey text-15'
        />

		<div class='grid lg:grid-cols-3 grid-cols-1 gap-3 mt-4'>
			<Input placeholder='Deal value' label='Deal value' name='quoted'
                groupClass='bg-grey text-15' 
                v-model:value='value.quoted'
                :focusedClass='true'
                @focus='focus' @blur='blur' @keyup='keyup'
                :hideError='true'
            >

                <template #left="{ isFocused }">
                	<p class='pl-3 pt-0.5 font-semibold block -mr-1 transition-colors'
                		:class='[isFocused ? "text-black" : "text-grey-900"]'
                		v-text='"£"' 
                	/>
                </template>
            </Input>

			<Input placeholder='Profit' label='Profit' name='profit'
				v-model:value='value.profit'
				:focusedClass='true'
                groupClass='bg-grey text-15' 
                @focus='focus' @blur='blur' @keyup='keyup'
                :hideError='true'
            >

                <template #left="{ isFocused }">
                	<p class='pl-3 pt-0.5 font-semibold block -mr-1 transition-colors'
                		:class='[isFocused ? "text-black" : "text-grey-900"]'
                		v-text='"£"' 
                	/>
                </template>
            </Input>

            <Input placeholder='Enter %' label='Margin' name='margin'
            	:focusedClass='true'
                groupClass='bg-grey text-15' 
                v-model:value='value.margin'
                @focus='focus' @blur='blur' @keyup='keyup'
                :hideError='true'
            >

                <template #right="{ isFocused }">
                	<p class='pl-3 pt-0.5 font-bold block transition-colors pr-2'
                		:class='[isFocused ? "text-black" : "text-grey-900"]'
                		v-text='"%"' 
                	/>
                </template>
            </Input>
        </div>

        <Error :error='errors.has("quoted")' />
        <Error :error='errors.has("profit")' />
        <Error :error='errors.has("margin")' />

        <Select class='col-span-3 mt-4' selectClass='!bg-grey text-15' menuClass='!bg-grey'
        	name='temperature' v-model:value='value.temperature'
			label='Deal temperature' placeholder='Select category' 
			:options='[
				{
					value: 80,
					html: `<div class="text-13 h-6 px-3 border bg-white border-red-700 text-red-700 inline-block pt-px">
					<span>Hot</span>
				</div>`
				},
				{
					value: 50,
					html: `<div class="text-13 h-6 px-3 border bg-white border-red-600 text-red-600 inline-block pt-px">
					<span>Warm</span>
				</div>`
				},
				{
					value: 20,
					html: `<div class="text-13 h-6 px-3 border bg-white border-blue-600 text-blue-600 inline-block pt-px">
					<span>Cold</span>
				</div>`
				}
			]'
		/>
	</div>
</template>

<script>
	import Deal from '~/models/deal.js'
	import Errors from '~/models/errors.js'

	export default {
		props: {
			size: Boolean,
			deal: {
				type: Object,
				default: null
			},
			errors: { type: Object, default: null }
		},

		created() {
			this.$event.$on('page-updated', () => {
				this.value = Deal.form()
			})
		},

		data() {
			return {
				value: Deal.form(this.deal),
				addName: this.deal?.name != null ? true : false
			}
		},

		methods: {
			focus(event) {
				switch (event.target.name) {
					case 'quoted':
						if (this.value.quoted != '' && this.value.quoted != null) {
							this.value.quoted = this.$help.dpToFloat(this.value.quoted)
						}
					break;
					case 'profit':
						if (this.value.profit != '' && this.value.profit != null) {
							this.value.profit = this.$help.dpToFloat(this.value.profit)
						}
					break;
					case 'margin':
						if (this.value.margin != '' && this.value.margin != null) {
							this.value.margin = this.$help.dpToFloat(this.value.margin)
						}
					break;
				}
			},

			keyup(event) {
				let canUpdate = false;

				if (((this.value.margin != '' ? 1 : 0) 
					+ (this.value.profit != '' ? 1 : 0) 
					+ (this.value.quoted != '' ? 1 : 0)) >= 2
				) {
					canUpdate = true
				}

				let q = this.value.quoted == '' ? null : this.$help.dpToFloat(this.value.quoted);
				let p = this.value.profit == '' ? null : this.$help.dpToFloat(this.value.profit);
				let m = this.value.margin == '' ? null : this.$help.dpToFloat(this.value.margin);

				if (canUpdate) {
					switch (true) {
						case event.target.name == 'quoted':
							if (p == null) {
								p = (q / 100) * m;
							} else {
								m = (100 / q) * p;
							}
						break;
						case event.target.name == 'profit':
							if (q == null) {
								q = (p / m) * 100
							} else { //  (m == null) 
								m = (100 / q) * p;
							}
						break;
						case event.target.name == 'margin':
							if (q == null) { 
								q = (p / m) * 100
							} else {
								p = (q / 100) * m;
							}
						break;
					}
				}

				this.value.quoted = q != null ? q : '';
				this.value.profit = p != null ? p : '';
				this.value.margin = m != null ? m : ''

				if (event.target.name != 'quoted') {
					this.value.quoted = q != null ? this.$help.dp(q, 2, false) : '';
				}

				if (event.target.name != 'profit') {
					this.value.profit = p != null ? this.$help.dp(p, 2, false) : '';
				}

				if (event.target.name != 'margin') {
					this.value.margin = m != null ? this.$help.dpToFloat(m).toFixed(2) : ''
				}
			},

			blur(event) {
				let q = this.value.quoted == '' ? null : this.$help.dpToFloat(this.value.quoted);
				let p = this.value.profit == '' ? null : this.$help.dpToFloat(this.value.profit);
				let m = this.value.margin == '' ? null : this.$help.dpToFloat(this.value.margin);

				this.value.quoted = q != null ? this.$help.dp(q, 2, false) : '';
				this.value.profit = p != null ? this.$help.dp(p, 2, false) : '';
				this.value.margin = m != null ? this.$help.dpToFloat(m).toFixed(2) : ''
			},
		},

		computed: {
			categories() {
				let { user } = useAuth()

				return user.deal_categories
			},

			options() {
				return this.categories.map((n) => {
					return {
						value: n.id,
						html: this.$help.capitalizeFirstLetter(n.name)
					}
				})
			}
		}
	}
</script>